import { forwardRef, useContext, useState } from 'react';
import type { FunctionComponent, PropsWithoutRef, ReactElement, Ref, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Icon,
  defaultThemeProp,
  useConsolidatedRef,
  useElement,
  isValidElement
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';

import { FeedContext } from '../Feed/Feed.context';

interface AutopilotProps {
  children: Record<string, string> | ReactElement;
}

const StyledAutopilotButton = styled(Button)(({ theme }) => {
  return css`
    color: ${theme.base.palette.ai};

    &:enabled:focus-visible,
    &:not([disabled]):focus-visible {
      box-shadow: none;
      text-decoration: underline;
    }

    svg {
      margin-inline-end: calc(0.5 * ${theme.base.spacing});
    }
  `;
});

StyledAutopilotButton.defaultProps = defaultThemeProp;

const serializer = new XMLSerializer();

const Autopilot: FunctionComponent<AutopilotProps & ForwardProps> = forwardRef(function Autopilot(
  { children, ...restProps }: PropsWithoutRef<AutopilotProps>,
  ref: Ref<HTMLButtonElement>
) {
  const {
    autopilotInfo: { label: autopilotLabel },
    onAutopilotClick
  } = useContext(FeedContext);
  const [autopilotContent, setAutopilotContent] = useState<JSX.Element | undefined>();
  const [target, setTarget] = useElement<HTMLButtonElement>(null);
  const mentionRef = useConsolidatedRef(ref);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    setTarget(e.currentTarget);
    const comp = onAutopilotClick?.({
      target: e.currentTarget,
      onDismiss: () => setTarget(null)
    });
    if (comp) setAutopilotContent(comp);
  };

  return (
    <>
      <StyledAutopilotButton
        {...restProps}
        ref={mentionRef}
        variant='link'
        onClick={handleClick}
        spellCheck={false}
      >
        <Icon name='polaris-solid' />
        {isValidElement(children) ? children : autopilotLabel}
      </StyledAutopilotButton>
      {target && autopilotContent}
    </>
  );
});

export const AutopilotConfig = {
  type: 'autopilot',
  xmlElement: 'pega-autopilot',
  regexPattern: /(?:^|)<pega-autopilot\/>/g,
  component: Autopilot,
  inject: () => {
    const element = document.createElementNS(null, 'pega-autopilot');
    return serializer.serializeToString(element);
  }
};

export default Autopilot;
